import { CircleUserRound, Menu, Moon, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, Route, Routes, useLocation } from "react-router";

import { probeAuditAccess, probeConfigurationAccess } from "./api/capabilities";
import { fetchMe, type Me } from "./api/me";
import { listTasks } from "./api/tasks";
import { SessionContext } from "./auth/sessionContext";
import markUrl from "./brand/qualify-mark-animated.svg";
import { strings } from "./brand/strings";
import { applyTheme, initialTheme, setTheme, type Theme } from "./brand/theme";
import type { Session } from "./auth/session";
import { ActionDetailPage, ActionsPage } from "./screens/Actions";
import { DashboardPage } from "./screens/DashboardPage";
import { PersonaHome } from "./screens/PersonaHome";
import { FindingDetailPage, FindingsPage } from "./screens/Findings";
import { GuidePage } from "./screens/GuidePage";
import { ConfigurationPage } from "./screens/ConfigurationPage";
import { InspectionDetailPage, InspectionsPage } from "./screens/Inspections";
import { ItpDetailPage, ItpsPage } from "./screens/Itps";
import { NotFoundPage } from "./screens/NotFoundPage";
import { ActivityDetailPage, ScheduleDetailPage, SchedulesPage } from "./screens/Schedules";
import { RfiDetailPage, RfisPage } from "./screens/Rfis";
import { CalendarPage } from "./calendar/CalendarPage";
import { EvaluationsPage } from "./screens/Evaluations";
import { LessonsPage } from "./screens/Lessons";
import { ProgramLens } from "./nav/ProgramLens";
import {
  ContractDetailPage,
  PartiesPage,
  ProjectDetailPage,
  ProjectsPage,
} from "./nav/PortfolioPages";
import { AliasRedirect } from "./nav/AliasRedirect";
import { CrumbContext, ShellBreadcrumbs, useCrumbState } from "./nav/crumbs";
import { useScrollRestoration } from "./nav/scrollRestoration";
import { Sidebar } from "./nav/Sidebar";
import { useShellLayout } from "./nav/useShellLayout";
import { NotificationBell } from "./notifications/NotificationBell";
import { AccountPage } from "./screens/AccountPage";
import { AuditPage } from "./screens/AuditPage";
import { OutboxPage } from "./screens/OutboxPage";
import { StandardsPage } from "./screens/StandardsPage";
import { CredentialsPage } from "./screens/CredentialsPage";
import { TasksPage } from "./screens/TasksPage";
import { WitnessDocketPage } from "./screens/WitnessDocketPage";
import { QuickActionsMenu } from "./nav/QuickActions";

interface ShellPageProps {
  readonly session: Session;
  readonly onSignOut: () => void;
}

type State = { kind: "loading" } | { kind: "ready"; me: Me } | { kind: "error" };

/** The rail state's localStorage key. A collapsed rail survives the reload. */
const RAIL_KEY = "qualify.sidebar.rail";

function initialRail(): boolean {
  return localStorage.getItem(RAIL_KEY) === "1";
}

/**
 * The signed-in application shell: the frame the working screens are built into.
 *
 * It carries the design language, the navigation and the identity, and nothing else.
 * What it does NOT carry is any opinion about what the signed-in person may reach: the
 * navigation is the persona shape plus the answers the API gives, and the one conditional
 * entry (configuration) renders only on the configuration probe's answer, which is the
 * server's, not this component's.
 *
 * The top bar (M5 P1b, ADR-027) carries the product identity on the left, and the
 * theme toggle, the notification bell and the account menu on the right. The rail
 * toggle lives inside the sidebar itself as an icon (M5 P5d).
 * The bell is wired since P2b: unread badge, dropdown, mark-read, 60s poll.
 *
 * THE FRAME (W-F shell wave, Mahmoud's orders 2026-10-09): one 100vh frame with no
 * page-level scrollbar. The side menu and the screen are two separate scroll
 * containers; neither moves the other. On a phone the side menu becomes a drawer
 * opened from the top bar, laid OVER the screen, never stacked above it.
 */
export function ShellPage({ session, onSignOut }: ShellPageProps): React.JSX.Element {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [configurationAllowed, setConfigurationAllowed] = useState(false);
  const [auditAllowed, setAuditAllowed] = useState(false);
  const [rail, setRail] = useState<boolean>(initialRail);
  const [theme, setThemeState] = useState<Theme>(initialTheme);
  const [accountOpen, setAccountOpen] = useState(false);
  const layout = useShellLayout();
  const [drawerOpenState, setDrawerOpen] = useState(false);
  const drawerOpen = layout === "phone" && drawerOpenState;
  const menuRef = useRef<HTMLButtonElement>(null);
  const drawerWasOpen = useRef(false);
  // Wave W2 (Mahmoud's UAT): Back restores the place on the screen, and one breadcrumb
  // trail sits under the bar on every screen, its record named by the page.
  const mainRef = useScrollRestoration();
  const crumbs = useCrumbState();

  // The chosen theme is applied on mount and after every explicit choice, so the
  // document element's data-theme is always the truth the stylesheet keys on.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const controller = new AbortController();
    fetchMe(session.accessToken, controller.signal)
      .then((me) => {
        setState({ kind: "ready", me });
      })
      .catch(() => {
        setState({ kind: "error" });
      });
    return () => {
      controller.abort();
    };
  }, [session.accessToken]);

  const location = useLocation();
  const [taskCount, setTaskCount] = useState<number | null>(null);

  // The open task count badged on the Tasks entry (LA W1.3), read once the account
  // has loaded and again on every page change, so a task closed by its action leaves
  // the badge on the next screen. A failed read shows no badge rather than a wrong one.
  const ready = state.kind === "ready";
  useEffect(() => {
    if (!ready) {
      return undefined;
    }
    const controller = new AbortController();
    listTasks(session.accessToken, { openOnly: true, limit: 1, signal: controller.signal })
      .then((body) => {
        // Wave Experience A: the badge counts what waits on me, not the notes.
        setTaskCount(body.actionable ?? body.open);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setTaskCount(null);
        }
      });
    return () => {
      controller.abort();
    };
  }, [session.accessToken, location.pathname, ready]);

  // Focus returns to the menu button when the drawer closes (it moved into the drawer
  // when it opened), so a keyboard or switch user is never stranded.
  useEffect(() => {
    if (drawerWasOpen.current && !drawerOpen) {
      menuRef.current?.focus();
    }
    drawerWasOpen.current = drawerOpen;
  }, [drawerOpen]);

  // The configuration probe. Until the configuration endpoints land it answers absence
  // for everyone without a network call, so nobody sees the navigation and nothing is
  // pretended. When the endpoints land, the answer is the server's, per principal.
  useEffect(() => {
    let live = true;
    probeConfigurationAccess(session.accessToken)
      .then((allowed) => {
        if (live) {
          setConfigurationAllowed(allowed);
        }
      })
      .catch(() => {
        if (live) {
          setConfigurationAllowed(false);
        }
      });
    return () => {
      live = false;
    };
  }, [session.accessToken]);

  // Wave AUD frontend item 2: the audit register probe (AUDIT_LOG READ).
  useEffect(() => {
    let live = true;
    probeAuditAccess(session.accessToken)
      .then((allowed) => {
        if (live) {
          setAuditAllowed(allowed);
        }
      })
      .catch(() => {
        if (live) {
          setAuditAllowed(false);
        }
      });
    return () => {
      live = false;
    };
  }, [session.accessToken]);

  function toggleRail(): void {
    setRail((value) => {
      // Persist the NEXT state: "1" is the rail.
      localStorage.setItem(RAIL_KEY, value ? "0" : "1");
      return !value;
    });
  }

  function chooseTheme(next: Theme): void {
    setThemeState(next);
    setTheme(next);
  }

  return (
    <SessionContext.Provider value={session}>
      <CrumbContext.Provider value={crumbs.feed}>
        <div className={drawerOpen ? "q-shell q-shell--drawer-open" : "q-shell"}>
          <header className="q-shell__bar" inert={drawerOpen}>
            {layout === "phone" && (
              <button
                ref={menuRef}
                type="button"
                className="q-shell__menu"
                aria-label={strings.nav.drawerOpen}
                aria-expanded={drawerOpen}
                aria-controls="q-sidebar-panel"
                data-testid="drawer-toggle"
                onClick={() => {
                  setDrawerOpen(true);
                }}
              >
                <Menu className="q-shell__menu-icon" aria-hidden="true" strokeWidth={1.75} />
              </button>
            )}
            {/* The logo and the wordmark lead Home (Mahmoud's order, 2026-10-09). */}
            <Link
              to="/"
              className="q-shell__brand"
              data-testid="brand-home"
              aria-label={`${strings.productName}: ${strings.nav.home}`}
              style={{ color: "inherit", textDecoration: "none" }}
            >
              <img className="q-shell__mark" src={markUrl} alt="" />
              <h1 className="q-wordmark q-shell__wordmark">{strings.productName}</h1>
            </Link>

            <div className="q-shell__identity">
              <QuickActionsMenu
                me={state.kind === "ready" ? state.me : null}
                configurationAllowed={configurationAllowed}
              />
              <button
                type="button"
                className="q-button q-button--quiet q-shell__theme-toggle"
                data-testid="theme-toggle"
                onClick={() => {
                  chooseTheme(theme === "light" ? "dark" : "light");
                }}
              >
                {theme === "light" ? (
                  <Moon className="q-shell__bar-icon" aria-hidden="true" strokeWidth={1.75} />
                ) : (
                  <Sun className="q-shell__bar-icon" aria-hidden="true" strokeWidth={1.75} />
                )}
                <span className="q-shell__bar-label">
                  {theme === "light" ? strings.shellUi.darkTheme : strings.shellUi.lightTheme}
                </span>
              </button>
              <NotificationBell token={session.accessToken} />
              <div className="q-account">
                <button
                  type="button"
                  className="q-button q-button--quiet"
                  aria-expanded={accountOpen}
                  aria-controls="q-account-menu"
                  data-testid="account-toggle"
                  onClick={() => {
                    setAccountOpen((value) => !value);
                  }}
                >
                  <CircleUserRound
                    className="q-shell__bar-icon"
                    aria-hidden="true"
                    strokeWidth={1.75}
                  />
                  {state.kind === "ready" && (
                    <span className="q-shell__who" data-testid="bar-identity">
                      {state.me.name}
                    </span>
                  )}
                  {state.kind !== "ready" && (
                    <span className="q-shell__bar-label">{strings.shellUi.accountMenu}</span>
                  )}
                </button>
                {accountOpen && (
                  <div className="q-account__menu" id="q-account-menu" data-testid="account-menu">
                    <button type="button" className="q-button q-button--quiet" onClick={onSignOut}>
                      {strings.shell.signOut}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <div className="q-shell__frame">
            <Sidebar
              configurationAllowed={configurationAllowed}
              auditAllowed={auditAllowed}
              workflowClass={state.kind === "ready" ? state.me.workflowClass : null}
              onSwitchPersona={onSignOut}
              rail={rail}
              onToggleRail={toggleRail}
              layout={layout}
              drawerOpen={drawerOpen}
              onCloseDrawer={() => {
                setDrawerOpen(false);
              }}
              taskCount={taskCount}
            />
            {drawerOpen && (
              <div
                className="q-shell__scrim"
                data-testid="drawer-scrim"
                aria-hidden="true"
                onClick={() => {
                  setDrawerOpen(false);
                }}
              />
            )}

            <main className="q-shell__main" id="q-content" inert={drawerOpen} ref={mainRef}>
              <div className="q-shell__content">
                {state.kind === "loading" && (
                  <p className="q-status" role="status">
                    {strings.shell.loading}
                  </p>
                )}
                {state.kind === "error" && (
                  <p className="q-alert" role="alert">
                    {strings.shell.failed}
                  </p>
                )}

                {state.kind === "ready" && <ShellBreadcrumbs recordLabel={crumbs.recordLabel} />}
                {state.kind === "ready" && (
                  <Routes>
                    <Route element={<Outlet context={{ me: state.me }} />}>
                      <Route index element={<PersonaHome me={state.me} />} />
                      <Route path="dashboard" element={<DashboardPage me={state.me} />} />
                      <Route path="itps" element={<ItpsPage />} />
                      <Route path="itps/:itpId" element={<ItpDetailPage />} />
                      <Route path="schedules" element={<SchedulesPage />} />
                      <Route path="schedules/:scheduleId" element={<ScheduleDetailPage />} />
                      <Route path="activities/:activityId" element={<ActivityDetailPage />} />
                      <Route path="rfis" element={<RfisPage />} />
                      <Route path="rfis/:rfiId" element={<RfiDetailPage />} />
                      <Route path="inspections" element={<InspectionsPage />} />
                      <Route
                        path="inspections/:verificationId"
                        element={<InspectionDetailPage />}
                      />
                      <Route path="findings" element={<FindingsPage />} />
                      <Route path="findings/:findingId" element={<FindingDetailPage />} />
                      <Route path="actions" element={<ActionsPage />} />
                      <Route path="actions/:actionRequestId" element={<ActionDetailPage />} />
                      <Route path="guide" element={<GuidePage />} />
                      <Route path="configuration" element={<ConfigurationPage />} />
                      <Route path="calendar" element={<CalendarPage />} />
                      <Route path="lessons" element={<LessonsPage />} />
                      <Route path="evaluations" element={<EvaluationsPage />} />
                      <Route path="portfolio/projects" element={<ProjectsPage />} />
                      <Route path="portfolio/projects/:projectId" element={<ProjectDetailPage />} />
                      <Route
                        path="portfolio/contracts/:contractId"
                        element={<ContractDetailPage />}
                      />
                      <Route path="portfolio/parties" element={<PartiesPage />} />
                      <Route path="tasks" element={<TasksPage />} />
                      <Route path="account" element={<AccountPage />} />
                      <Route path="audit" element={<AuditPage />} />
                      <Route path="outbox" element={<OutboxPage />} />
                      <Route path="standards" element={<StandardsPage />} />
                      <Route path="credentials" element={<CredentialsPage />} />
                      <Route path="docket" element={<WitnessDocketPage />} />
                      {/* R07 (wave W0): the alias routes are retired. A stored notification
                      or a bookmark at an alias still lands, redirected to the one route. */}
                      <Route path="projects/:projectId" element={<AliasRedirect />} />
                      <Route path="contracts/:contractId" element={<AliasRedirect />} />
                      <Route path="parties" element={<AliasRedirect />} />
                      <Route path="parties/:partyId" element={<AliasRedirect />} />
                      <Route path="verifications/:verificationId" element={<AliasRedirect />} />
                      <Route path="action-requests/:actionRequestId" element={<AliasRedirect />} />
                      <Route
                        path="programs/engineering"
                        element={
                          <ProgramLens title={strings.nav.programEngineering} lens="engineering" />
                        }
                      />
                      <Route
                        path="programs/procurement"
                        element={
                          <ProgramLens title={strings.nav.programProcurement} lens="procurement" />
                        }
                      />
                      <Route
                        path="programs/construction"
                        element={
                          <ProgramLens
                            title={strings.nav.programConstruction}
                            lens="construction"
                          />
                        }
                      />
                      <Route
                        path="programs/external-audits"
                        element={
                          <ProgramLens
                            title={strings.nav.programExternalAudits}
                            lens="externalAudits"
                          />
                        }
                      />
                      <Route path="*" element={<NotFoundPage />} />
                    </Route>
                  </Routes>
                )}
              </div>
            </main>
          </div>
        </div>
      </CrumbContext.Provider>
    </SessionContext.Provider>
  );
}
